import { PropertiesPanel } from '../../../../../../widgets/propertiesPanel';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import styled, { createGlobalStyle } from 'styled-components';
import { ObjectConfigHelpers, ObjectNode, useObjectPropertiesCreator } from '../objectEditorConfig';

// CSS globale per gli input degli overlay components
const OverlayInputStyles = createGlobalStyle`
	.object-overlay-properties input[type="number"],
	.object-overlay-properties input[type="text"] {
		width: 80px !important;
		min-width: 80px !important;
		max-width: 80px !important;
	}
	
	.object-overlay-properties [data-testid*="coordinate"] input,
	.object-overlay-properties [class*="coordinate"] input {
		width: 80px !important;
		min-width: 80px !important;
	}
`;

const PanelContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    border-radius: 8px;
    background: #fff;
    max-height: 100%;
`;

const Title = styled.h3`
    display: flex;
    align-items: center;
    gap: 5px;
    margin: 0 0 16px 0;
`;

const PropertiesPanelContainer = styled.div`
    flex: 1;
    overflow-y: auto;
`;

export interface ObjectOverlayProps {
    node: ObjectNode;
    sceneEditor: any;
    onPropertiesUpdate: (updates: Partial<ObjectNode>) => void;
    onPropertyChanged?: (property: string, value: any) => void;
}

const ObjectOverlayComponentNew: FC<ObjectOverlayProps> = ({ node, sceneEditor, onPropertiesUpdate, onPropertyChanged }) => {
    // Stato interno per mantenere i valori durante l'editing
    const [localState, setLocalState] = useState<ObjectNode>({
        ...node,
        position: [...node.position] as [number, number, number],
        rotation: [...node.rotation] as [number, number, number],
        scaling: [...node.scaling] as [number, number, number]
    });

    const isVTOEnabledInScene = sceneEditor?.getTryOnEnabled?.() ?? false;
    const currentNodeIdRef = useRef(node.id);

    // Per nodi root, controlla se è una mesh padre e leggi i finetuning settings
    const [fineTuningSettings, setFineTuningSettings] = useState<any>(undefined);

    // Verifica se è un nodo root o transform basandosi sui metadata
    const isRootNode = node.metadata?.isRootNode === true;
    const isTransformNode = node.metadata?.isTransformNode === true;

    // Carica i finetuning settings per nodi root
    useEffect(() => {
        if (isRootNode && node.data && sceneEditor) {
            let isFatherMesh = false;
            let firstSettings: any = undefined;

            // Controlla se è una mesh padre (ha children)
            if (node.data && typeof node.data.getChildMeshes === 'function' && node.data.getChildMeshes().length > 0) {
                isFatherMesh = true;
                const mesh = node.data;
                firstSettings = sceneEditor.getMeshFineTuningData?.(mesh) ?? undefined;

                console.log('🔍 Root node is father mesh, loading finetuning settings:', {
                    meshName: mesh.name,
                    hasChildren: mesh.getChildMeshes().length,
                    settings: firstSettings
                });
            }

            setFineTuningSettings(firstSettings);
        }
    }, [isRootNode, node.data, sceneEditor]);

    // Sincronizza lo stato interno SOLO quando cambia il nodo selezionato
    useEffect(() => {
        if (currentNodeIdRef.current !== node.id) {
            console.log('Node changed, syncing local state from:', currentNodeIdRef.current, 'to:', node.id);
            currentNodeIdRef.current = node.id;

            setLocalState({
                ...node,
                position: [...node.position] as [number, number, number],
                rotation: [...node.rotation] as [number, number, number],
                scaling: [...node.scaling] as [number, number, number]
            });
        }
    }, [node.id]);

    // Helper per aggiornare sia lo stato locale che quello esterno
    const updateProperty = useCallback(
        (updates: Partial<ObjectNode>) => {
            // Validazione speciale per il nome
            if ('name' in updates && updates.name !== undefined) {
                updates.name = (updates.name || '').trim();
            }

            // Aggiorna lo stato locale immediatamente
            setLocalState((prev: ObjectNode) => ({ ...prev, ...updates }));

            // Aggiorna lo stato esterno
            onPropertiesUpdate(updates);
        },
        [onPropertiesUpdate]
    );

    // Handler per i pulsanti di finetuning
    const handleVirtualTryonFinetuning = useCallback(() => {
        console.log('🎯 Virtual Try-on Finetuning clicked for node:', node.name);

        const mesh = node.data; // La mesh di Babylon dal nodo
        if (!mesh) {
            console.error('Mesh not available in node data');
            return;
        }

        try {
            // Verifica se ci sono già impostazioni VTO configurate tramite sceneEditor
            const tryOnSettings = sceneEditor.getTryOnSettings();
            console.log('🔧 Current VTO settings:', tryOnSettings);

            // Se non ci sono impostazioni VTO, mostra un avviso
            if (!tryOnSettings || !tryOnSettings.type) {
                console.warn('⚠️ No VTO settings found. Please configure VTO type and provider first.');
                alert('Please configure VTO type and provider in the VTO Editor first before using fine tuning.');
                return;
            }

            // Determina quale provider usare basandosi sul tipo VTO
            const vtoType = tryOnSettings.type;
            const vtoProviderFromSettings = (tryOnSettings as any).provider || (tryOnSettings as any).providerID;

            console.log('🎭 Detected VTO configuration:', {
                type: vtoType,
                providerFromSettings: vtoProviderFromSettings
            });

            // Logica per determinare quale sistema di fine tuning usare
            let useDeepAR = false;
            let useClassicVtoFinetuning = false;
            let useTryOnFineTuning = false;

            // Provider DeepAR (id 3) per shoes (TryOnType.Shoes = 6)
            if (vtoProviderFromSettings === 3 || vtoType === 6) {
                useDeepAR = true;
                console.log('🦶 Using DeepAR system for shoes/DeepAR provider');
            }
            // Provider Banuba (id 2) o Mazing (id 1) per altri tipi
            else if (vtoProviderFromSettings === 2 || vtoProviderFromSettings === 1) {
                useTryOnFineTuning = true;
                console.log('👤 Using TryOnFineTuning system for Banuba/Mazing provider');
            }
            // Fallback al sistema classico VtoFinetuning
            else {
                useClassicVtoFinetuning = true;
                console.log('🔧 Using classic VtoFinetuning system as fallback');
            }

            // Rimuovi tutti i listener esistenti
            sceneEditor.removeAllFineTuningSettingListeners?.();

            // Per DeepAR, non attiviamo il sistema di finetuning del sceneEditor
            if (!useDeepAR) {
                // Abilita il fine tuning solo per provider non-DeepAR
                sceneEditor.showFineTuning = true;
            }

            // Se è un nodo root con settings esistenti, usali come punto di partenza
            let initialSettings: any = undefined;
            if (isRootNode && fineTuningSettings) {
                initialSettings = fineTuningSettings;
                console.log('🔄 Using existing finetuning settings for root node:', initialSettings);
            }

            // Aggiungi listener per i cambiamenti delle impostazioni solo per provider non-DeepAR
            if (!useDeepAR) {
                sceneEditor.addFineTuningSettingListener?.((settings: any) => {
                    sceneEditor.setMeshFineTuningData?.(mesh, settings);
                    sceneEditor.setProviderFineTuningData?.({
                        settings: settings
                    });

                    // Aggiorna lo stato locale dei settings
                    setFineTuningSettings(settings);
                });
            }

            // Esporta la mesh in formato GLB
            sceneEditor
                .exportMeshToGLBBlob?.(mesh.id)
                .then(async (blob: Blob) => {
                    if (!blob) {
                        console.error('Failed to export mesh to blob');
                        return;
                    }

                    const currentSettings = initialSettings || (sceneEditor.getMeshFineTuningData?.(mesh) ?? undefined);

                    // A seconda del provider, avvia il sistema appropriato
                    if (useDeepAR) {
                        console.log('🦶 Launching DeepAR fine tuning system...');
                        // TODO: Implementare DeepAR specifico se necessario
                    } else if (useTryOnFineTuning) {
                        console.log('👤 Launching TryOnFineTuning system...');
                        // TODO: Implementare TryOnFineTuning specifico se necessario
                    } else {
                        console.log('🔧 Launching classic VtoFinetuning system...');
                        // TODO: Implementare sistema classico se necessario
                    }

                    // Per provider non-DeepAR, abilita il fine tuning nel sceneEditor
                    if (!useDeepAR) {
                        // TODO: Implementare logica specifica se necessario
                    }
                })
                .catch((error: any) => {
                    console.error('Error exporting mesh for finetuning:', error);
                });
        } catch (error) {
            console.error('Error starting finetuning:', error);
        }
    }, [node, sceneEditor, isRootNode, fineTuningSettings]);

    const handleResetFinetuningSettings = useCallback(() => {
        console.log('🔄 Reset Finetuning Settings clicked for node:', node.name);

        const mesh = node.data; // La mesh di Babylon dal nodo
        if (!mesh) {
            console.error('Mesh not available in node data');
            return;
        }

        try {
            // Reset dei dati del provider
            sceneEditor.resetProviderFineTuningData?.();

            // Reset dei metadata della mesh
            if (mesh.metadata && mesh.metadata.tryOnFineTuningSettings) {
                mesh.metadata.tryOnFineTuningSettings = undefined;
            }

            // Reset dello stato locale dei settings
            setFineTuningSettings(undefined);

            // Notifica il cambiamento
            if (onPropertyChanged) {
                onPropertyChanged('tryOnFineTuningSettings', undefined);
            }

            console.log('✅ Finetuning settings reset successfully for node:', node.name);
        } catch (error) {
            console.error('Error resetting finetuning settings:', error);
        }
    }, [node, sceneEditor, onPropertyChanged]);

    // Crea gli helpers per il PropertiesPanel
    const helpers: ObjectConfigHelpers = {
        sceneEditor,
        updateProperty,
        onPropertyChanged,
        isVTOEnabledInScene,
        fineTuningSettings,
        handleVirtualTryonFinetuning,
        handleResetFinetuningSettings
    };

    const objectPropertiesCreator = useObjectPropertiesCreator();

    // Se non abbiamo i dati del nodo, mostra un messaggio
    if (!node) {
        return (
            <PanelContainer>
                <Title>{T._('Object Properties', 'Admin')}</Title>
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>{T._('No object selected', 'Admin')}</div>
            </PanelContainer>
        );
    }

    return (
        <>
            <OverlayInputStyles />
            <PanelContainer>
                <Title>{T._('Object Properties', 'Admin')}</Title>
                <PropertiesPanelContainer className='object-overlay-properties'>
                    <PropertiesPanel
                        properties={[
                            {
                                type: 'group',
                                id: localState.id,
                                header: localState.name,
                                defaultIsOpen: true,
                                properties: objectPropertiesCreator(localState, helpers)
                            }
                        ]}
                    />
                </PropertiesPanelContainer>
            </PanelContainer>
        </>
    );
};

export default ObjectOverlayComponentNew;
